import React, { useState, useEffect, useRef } from 'react';
import AppNav from './AppNav';
import { useRipple } from '../hooks/useRipple';
import AdUnit from './AdUnit';
import './styles/clickEffects.css';

const IconBrain = ({ size = 13, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
    <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
  </svg>
);

const IconGlobe = ({ size = 13, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const IconTrendingUp = ({ size = 11, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
    <polyline points="17 6 23 6 23 12"/>
  </svg>
);

const IconTrendingDown = ({ size = 11, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
    <polyline points="17 18 23 18 23 12"/>
  </svg>
);

const IconMinus = ({ size = 11, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const IconWarning = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconRefresh = ({ size = 11, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
    <polyline points="23 4 23 10 17 10"/>
    <polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
  </svg>
);

const IconSearch = ({ size = 13, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const IconSwap = ({ size = 11, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9"/>
    <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
    <polyline points="7 23 3 19 7 15"/>
    <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
  </svg>
);

const COMMODITIES_30 = [
  { name: 'Bananas',                       unit: '/bunch',    base: 1200  },
  { name: 'Beans (red)',                   unit: '/kg',       base: 950   },
  { name: 'Beans (white)',                 unit: '/kg',       base: 900   },
  { name: 'Bread',                         unit: '/500g',     base: 1200  },
  { name: 'Cassava meal (gari yellow)',    unit: '/kg',       base: 822   },
  { name: 'Cowpeas',                       unit: '/kg',       base: 1260  },
  { name: 'Eggs',                          unit: '/dozen',    base: 3200  },
  { name: 'Fish',                          unit: '/kg',       base: 4000  },
  { name: 'Fuel (diesel)',                 unit: '/litre',    base: 1875  },
  { name: 'Fuel (petrol/gasoline)',        unit: '/litre',    base: 1310  },
  { name: 'Groundnuts',                   unit: '/kg',       base: 450   },
  { name: 'Maize',                         unit: '/kg',       base: 820   },
  { name: 'Maize flour',                   unit: '/kg',       base: 950   },
  { name: 'Meat (beef)',                   unit: '/kg',       base: 6200  },
  { name: 'Meat (goat)',                   unit: '/kg',       base: 6800  },
  { name: 'Milk powder',                   unit: '/400g',     base: 1250  },
  { name: 'Millet',                        unit: '/kg',       base: 600   },
  { name: 'Oil (palm)',                    unit: '/litre',    base: 440   },
  { name: 'Oil (vegetable)',               unit: '/litre',    base: 380   },
  { name: 'Onions',                        unit: '/kg',       base: 1450  },
  { name: 'Oranges',                       unit: '/dozen',    base: 1800  },
  { name: 'Rice (imported)',               unit: '/kg',       base: 1220  },
  { name: 'Rice (local)',                  unit: '/kg',       base: 1900  },
  { name: 'Salt',                          unit: '/kg',       base: 500   },
  { name: 'Sorghum',                       unit: '/kg',       base: 550   },
  { name: 'Spinach',                       unit: '/bunch',    base: 400   },
  { name: 'Sugar',                         unit: '/kg',       base: 1800  },
  { name: 'Tomatoes',                      unit: '/kg',       base: 1300  },
  { name: 'Watermelons',                   unit: '/fruit',    base: 3800  },
  { name: 'Yam',                           unit: '/tuber',    base: 3800  },
];

const ZONE = {
  'North-West':  ['Jigawa','Kaduna','Kano','Katsina','Kebbi','Sokoto','Zamfara'],
  'North-East':  ['Adamawa','Bauchi','Borno','Gombe','Taraba','Yobe'],
  'North-Central':['Benue','FCT (Abuja)','Kogi','Kwara','Nasarawa','Niger','Plateau'],
  'South-West':  ['Ekiti','Lagos','Ogun','Ondo','Osun','Oyo'],
  'South-East':  ['Abia','Anambra','Ebonyi','Enugu','Imo'],
  'South-South': ['Akwa Ibom','Bayelsa','Cross River','Delta','Edo','Rivers'],
};
const ZONE_MUL = {
  'Rice (local)':      {'North-East':0.88,'North-West':0.92,'North-Central':1.07,'South-West':1.05,'South-East':1.22,'South-South':1.10},
  'Rice (imported)':   {'North-East':0.90,'North-West':0.94,'North-Central':1.00,'South-West':1.05,'South-East':1.18,'South-South':1.12},
  'Maize':             {'North-East':0.80,'North-West':0.82,'North-Central':0.88,'South-West':1.05,'South-East':1.20,'South-South':1.12},
  'Beans (red)':       {'North-East':0.78,'North-West':0.80,'North-Central':0.95,'South-West':1.12,'South-East':1.18,'South-South':1.10},
  'Beans (white)':     {'North-East':0.78,'North-West':0.80,'North-Central':0.95,'South-West':1.12,'South-East':1.18,'South-South':1.10},
  'Yam':               {'North-East':0.88,'North-West':0.92,'North-Central':0.95,'South-West':1.05,'South-East':1.08,'South-South':1.10},
  'Onions':            {'North-East':0.55,'North-West':0.50,'North-Central':0.85,'South-West':1.10,'South-East':1.35,'South-South':1.25},
  'Tomatoes':          {'North-East':0.60,'North-West':0.62,'North-Central':0.85,'South-West':1.00,'South-East':1.28,'South-South':1.20},
  'Oil (palm)':        {'North-East':1.20,'North-West':1.18,'North-Central':1.10,'South-West':1.05,'South-East':0.85,'South-South':0.70},
  'Groundnuts':        {'North-East':0.82,'North-West':0.82,'North-Central':0.90,'South-West':1.05,'South-East':1.10,'South-South':1.08},
  'Meat (beef)':       {'North-East':0.90,'North-West':0.92,'North-Central':0.98,'South-West':1.05,'South-East':1.08,'South-South':1.10},
  'Fish':              {'North-East':1.05,'North-West':1.08,'North-Central':1.02,'South-West':0.98,'South-East':0.92,'South-South':0.88},
  'Sorghum':           {'North-East':0.78,'North-West':0.80,'North-Central':0.92,'South-West':1.08,'South-East':1.20,'South-South':1.15},
  'Millet':            {'North-East':0.75,'North-West':0.78,'North-Central':0.90,'South-West':1.10,'South-East':1.25,'South-South':1.18},
};
const SEASONAL = {
  'Yam':     [1.20,1.25,1.20,1.15,1.05,1.00,0.95,0.90,0.88,0.90,0.95,1.10],
  'Tomatoes':[1.15,1.10,1.05,1.00,1.05,1.10,1.00,0.90,0.88,0.85,0.88,1.00],
  'Maize':   [1.10,1.15,1.12,1.08,1.05,0.95,0.90,0.85,0.88,0.90,0.95,1.05],
  'Onions':  [0.95,0.92,0.90,0.95,1.05,1.10,1.08,1.05,1.00,0.95,0.90,0.92],
};

function getZone(state) {
  for (const [zone, states] of Object.entries(ZONE)) {
    if (states.includes(state)) return zone;
  }
  return 'South-West';
}

function estimatePrice(name, state = 'Lagos', monthNum = 4, year = 2026) {
  const c = COMMODITIES_30.find(x => x.name === name);
  let base = c?.base ?? 1000;
  const zone = getZone(state);
  const mul = ZONE_MUL[name]?.[zone] ?? 1.0;
  base = base * mul;
  const seasonal = SEASONAL[name]?.[monthNum - 1] ?? 1.0;
  base = base * seasonal;
  const inflationRate = name.startsWith('Fuel') ? 0.08 : 0.12;
  base = base * Math.pow(1 + inflationRate, year - 2026);
  return Math.round(base);
}

const WFP_MAP = {
  'Rice (local)':   'Rice',
  'Rice (imported)':'Rice',
  'Maize':          'Maize',
  'Maize flour':    'Maize meal',
  'Beans (red)':    'Beans',
  'Beans (white)':  'Beans',
  'Cowpeas':        'Cowpeas',
  'Yam':            'Yam',
  'Cassava meal (gari yellow)': 'Gari',
  'Onions':         'Onions',
  'Tomatoes':       'Tomatoes',
  'Oil (palm)':     'Oil (palm)',
  'Oil (vegetable)':'Oil (vegetable)',
  'Groundnuts':     'Groundnuts',
  'Sorghum':        'Sorghum',
  'Millet':         'Millet',
  'Sugar':          'Sugar',
  'Salt':           'Salt',
  'Meat (beef)':    'Beef',
  'Fish':           'Fish (dried)',
  'Eggs':           'Eggs',
  'Bread':          'Bread',
  'Fuel (petrol/gasoline)': 'Petrol',
  'Fuel (diesel)':  'Diesel',
};

const COMMODITY_PHOTOS = {
  'Bananas':                    'https://images.unsplash.com/photo-1603833665858-e61d17a86224?w=80&q=60',
  'Beans (red)':                'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSpmE0L5bTiyYltnoNnLpmUr-67YiVCje3DZg&s',
  'Beans (white)':              'https://i0.wp.com/www.africopanigeria.com/wp-content/uploads/2021/02/Pure-white-beans.png?fit=600%2C600&ssl=1',
  'Bread':                      'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=80&q=60',
  'Cassava meal (gari yellow)': 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSJuaMUZ8WoYR9SsrYV698swMeIicWiUwY4dg&s',
  'Cowpeas':                    'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQxhU8obh3-z5MqZH1R4KT1-_jyfJ6yzsE1AA&s',
  'Eggs':                       'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=80&q=60',
  'Fish':                       'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSuTuO7vC9ADgBrMJc_eBufJZJTo0fa8nMSZw&s',
  'Fuel (diesel)':              'https://5.imimg.com/data5/DX/LI/CY/ANDROID-46302579/product-jpeg-500x500.jpg',
  'Fuel (petrol/gasoline)':     'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTue-iBfUj5T6PnKMXO1IM2P9TSmYdDJ5Sgxg&s',
  'Groundnuts':                 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTutUfAvTJRos_uo1MOPUM1ekNZikDupb49Vg&s',
  'Maize':                      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSEPGaAsYQc-OLxMUtc_P7oIy3EctP-Z0vOAw&s',
  'Maize flour':                'https://cdn.tridge.com/attachment-file/69/3b/aa/693baae4cf68421c6e2ef1d54a311dc488ce028d/Maize_Flour.jpg',
  'Meat (beef)':                'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTogkXgjJVKOAKOXt1PSGR9oqVmByNjFkzVGA&s',
  'Meat (goat)':                'https://yourfreshbox.in/wp-content/uploads/2021/11/mut-shoulder-front.jpg',
  'Milk powder':                'https://images.ctfassets.net/yzlb24ni1fxz/6BAaNAV376srY8MAPPReoC/3d41288a1691d5d4e0f0ecc68c71b15b/Lactorich_Vers_links_image_square.jpg?fm=webp&q=75&w=2048',
  'Millet':                     'https://img.freepik.com/free-photo/raw-organic-healthy-millet_114579-9027.jpg?semt=ais_hybrid&w=740&q=80',
  'Oil (palm)':                 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTAnQc2finQTy-tMJZODLITcpgM-ZcPtaM60A&s',
  'Oil (vegetable)':            'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQVsNnJa_rFZXD7QU_nyfZkxzTzPolWfrH2lg&s',
  'Onions':                     'https://images.unsplash.com/photo-1508747703725-719777637510?w=80&q=60',
  'Oranges':                    'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS5bCYpYSse-rEyqEN84lh7HCbmckMQylw4kw&s',
  'Rice (imported)':            'https://img.freepik.com/premium-photo/rice-bag-white-background_269543-952.jpg?semt=ais_hybrid&w=740&q=80',
  'Rice (local)':               'https://img.freepik.com/premium-photo/rice-bag-white-background_269543-952.jpg?semt=ais_hybrid&w=740&q=80',
  'Salt':                       'https://i0.wp.com/media.globalnews.ca/videostatic/1017/31/GN160426VOOSHY.jpg?w=1040&quality=70&strip=all',
  'Sorghum':                    'https://www.eatthis.com/wp-content/uploads/sites/4/2022/11/sorghum-grain-bowl.jpg?quality=82&strip=all',
  'Spinach':                    'https://images.unsplash.com/photo-1576045057995-568f588f82fb?w=80&q=60',
  'Sugar':                      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTACBYcMHhXRm4P1jW8f5zxtFeCQbXuPWdxaQ&s',
  'Tomatoes':                   'https://upload.wikimedia.org/wikipedia/commons/8/89/Tomato_je.jpg',
  'Watermelons':                'https://images.unsplash.com/photo-1587049352846-4a222e784d38?w=80&q=60',
  'Yam':                        'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQz303YgCs0VC6gZvdI2ucO7g9ZruqM05J3PA&s',
};

const CATEGORY_MAP = {
  'Grains':     ['Maize','Maize flour','Millet','Rice (imported)','Rice (local)','Sorghum'],
  'Legumes':    ['Beans (red)','Beans (white)','Cowpeas','Groundnuts'],
  'Tubers':     ['Cassava meal (gari yellow)','Yam'],
  'Protein':    ['Eggs','Fish','Meat (beef)','Meat (goat)','Milk powder'],
  'Produce':    ['Bananas','Onions','Oranges','Spinach','Tomatoes','Watermelons'],
  'Staples':    ['Bread','Oil (palm)','Oil (vegetable)','Salt','Sugar'],
  'Fuel':       ['Fuel (diesel)','Fuel (petrol/gasoline)'],
};

const NOW = new Date();
const CURRENT_MONTH = NOW.getMonth() + 1;
const CURRENT_YEAR  = NOW.getFullYear();
const MONTH_NAMES   = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function SkeletonRow() {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderBottom:'1px solid rgba(10,46,20,0.06)' }}>
      <div style={{ width:52, height:52, borderRadius:10, background:'linear-gradient(90deg,#f0ede8 25%,#e5e2dd 50%,#f0ede8 75%)', backgroundSize:'200% 100%', animation:'ppShimmer 1.4s infinite', flexShrink:0 }} />
      <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6 }}>
        <div style={{ height:11, width:'55%', borderRadius:4, background:'linear-gradient(90deg,#f0ede8 25%,#e5e2dd 50%,#f0ede8 75%)', backgroundSize:'200% 100%', animation:'ppShimmer 1.4s infinite' }} />
        <div style={{ height:9, width:'30%', borderRadius:4, background:'linear-gradient(90deg,#f0ede8 25%,#e5e2dd 50%,#f0ede8 75%)', backgroundSize:'200% 100%', animation:'ppShimmer 1.4s infinite' }} />
      </div>
      <div style={{ width:80, height:22, borderRadius:4, background:'linear-gradient(90deg,#f0ede8 25%,#e5e2dd 50%,#f0ede8 75%)', backgroundSize:'200% 100%', animation:'ppShimmer 1.4s infinite' }} />
    </div>
  );
}

const IconPredict = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);

function PriceRow({ item, index, comparePrice, showCompare, onPredictClick }) {
  const rising  = (item.pct ?? 0) > 0;
  const falling = (item.pct ?? 0) < 0;
  const trendColor = falling ? '#2563eb' : rising ? '#dc2626' : '#6b7280';
  const photo = COMMODITY_PHOTOS[item.name];
  const [btnHover, setBtnHover] = useState(false);

  const diff = showCompare && comparePrice != null
    ? comparePrice - item.price
    : null;
  const diffPct = diff != null && item.price > 0
    ? ((diff / item.price) * 100).toFixed(1)
    : null;

  return (
    <div
      style={{
        display:'flex', alignItems:'center', gap:12,
        padding:'11px 16px',
        borderBottom:'1px solid rgba(10,46,20,0.06)',
        background: index % 2 === 0 ? '#ffffff' : '#fafaf8',
        transition:'background 0.15s',
        animation:`ppFadeIn 0.35s ease ${Math.min(index * 30, 400)}ms both`,
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#f5f2ed'}
      onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? '#ffffff' : '#fafaf8'}
    >
      <div style={{ width:52, height:52, borderRadius:10, overflow:'hidden', flexShrink:0, background:'#f0ede8' }}>
        {photo && (
          <img src={photo} alt={item.name}
            style={{ width:'100%', height:'100%', objectFit:'cover' }}
            onError={e => { e.currentTarget.style.display='none'; }}
          />
        )}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#001805', fontFamily:'Epilogue, sans-serif', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {item.name}
        </div>
        <div style={{ fontSize:10, color:'#727970', fontFamily:'Space Grotesk, sans-serif', textTransform:'uppercase', letterSpacing:'0.04em' }}>
          {item.unit}
          {item.market && <span style={{ color:'#b45309', marginLeft:6 }}>· {item.market}</span>}
        </div>
      </div>
      <div style={{ textAlign:'right', flexShrink:0 }}>
        <div style={{ fontFamily:'monospace', fontSize:15, fontWeight:800, color:'#001805', letterSpacing:'-0.3px' }}>
          ₦{item.price.toLocaleString()}
        </div>
        {item.pct != null && (
          <div style={{ fontSize:10, fontWeight:700, color:trendColor, fontFamily:'Space Grotesk, sans-serif', display:'flex', alignItems:'center', justifyContent:'flex-end', gap:3 }}>
            {falling
              ? <IconTrendingDown size={10} color={trendColor} />
              : rising
              ? <IconTrendingUp size={10} color={trendColor} />
              : <IconMinus size={10} color={trendColor} />
            }
            {Math.abs(item.pct).toFixed(1)}%
          </div>
        )}
      </div>
      {showCompare && diff != null && (
        <div style={{
          width:72, textAlign:'right', flexShrink:0,
          fontSize:10, fontWeight:700, fontFamily:'Space Grotesk, sans-serif',
          color: diff > 0 ? '#dc2626' : diff < 0 ? '#2563eb' : '#6b7280',
        }}>
          {diff > 0 ? '+' : ''}{Math.round(diff).toLocaleString()}
          <div style={{ fontSize:9, opacity:0.75 }}>
            ({diff > 0 ? '+' : ''}{diffPct}%)
          </div>
        </div>
      )}

      {/* ── PREDICT BUTTON ── */}
      <button
        title={`Predict ${item.name} price`}
        onClick={() => onPredictClick && onPredictClick(item.name)}
        onMouseEnter={() => setBtnHover(true)}
        onMouseLeave={() => setBtnHover(false)}
        style={{
          flexShrink: 0,
          width: 38, height: 38,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: btnHover
            ? 'linear-gradient(145deg,#1a4a2e,#0a2e14)'
            : 'linear-gradient(145deg,#0d3318,#1c4f35)',
          border: `2px solid ${btnHover ? '#f0c030' : '#d4a017'}`,
          borderRadius: 0,
          cursor: 'pointer',
          transition: 'all 0.18s ease',
          transform: btnHover ? 'translateY(-2px) scale(1.06)' : 'none',
          boxShadow: btnHover
            ? '0 6px 18px rgba(10,46,20,0.35), 3px 3px 0 rgba(212,160,23,0.3)'
            : '2px 2px 0 rgba(10,46,20,0.2)',
        }}
      >
        <IconPredict size={15} color={btnHover ? '#f0c030' : '#d4a017'} />
      </button>
    </div>
  );
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

export default function PricesPanel({ onBack, priceMode = 'student', onModeToggle, onNavigate, user = null, onUserChange }) {
  const [activeTab,   setActiveTab]   = useState('prediction');
  const [activeCategory, setActiveCategory] = useState('All');
  const [showCompare, setShowCompare] = useState(false);
  const triggerRipple = useRipple();
  const isMobile = useIsMobile();
  const navH = isMobile ? 60 : 72;

  const [predPrices,  setPredPrices]  = useState([]);
  const [predLoading, setPredLoading] = useState(true);
  const [predSource,  setPredSource]  = useState('');

  const [realPrices,  setRealPrices]  = useState([]);
  const [realLoading, setRealLoading] = useState(false);
  const [realError,   setRealError]   = useState(null);
  const [realFetched, setRealFetched] = useState(false);

  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  // ── Predict tooltip: shown once per session when user first clicks predict ──
  const [predictTooltip, setPredictTooltip] = useState(null); // { name } or null
  const tooltipShownRef = useRef(false);

  function handlePredictClick(commodityName) {
    if (!tooltipShownRef.current) {
      tooltipShownRef.current = true;
      setPredictTooltip({ name: commodityName });
    } else {
      onNavigate && onNavigate('predict', { initialCommodity: commodityName });
    }
  }

  function confirmPredictNavigate(commodityName) {
    setPredictTooltip(null);
    onNavigate && onNavigate('predict', { initialCommodity: commodityName });
  }

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 7000);
  };

  const [searchQ, setSearchQ] = useState('');

  useEffect(() => {
    fetchPredictions();
  }, []);

  async function fetchPredictions() {
    setPredLoading(true);
    const results = [];
    let fromModel = false;

    try {
      const sampleRes = await fetch('http://localhost:5000/api/predict', {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ commodity:'Rice (local)', state:'Lagos', month:CURRENT_MONTH, year:CURRENT_YEAR }),
        signal: AbortSignal.timeout(3000),
      });
      if (sampleRes.ok) fromModel = true;
    } catch (_) {}

    if (fromModel) {
      const fetches = COMMODITIES_30.map(c =>
        fetch('http://localhost:5000/api/predict', {
          method:'POST',
          headers:{ 'Content-Type':'application/json' },
          body: JSON.stringify({ commodity:c.name, state:'Lagos', month:CURRENT_MONTH, year:CURRENT_YEAR }),
          signal: AbortSignal.timeout(8000),
        })
        .then(r => r.json())
        .then(d => ({
          name:  c.name,
          unit:  c.unit,
          price: d.predicted_ngn || d.predicted_price || estimatePrice(c.name, 'Lagos', CURRENT_MONTH, CURRENT_YEAR),
          pct:   d.change_pct   || d.pct_change     || 0,
        }))
        .catch(() => ({
          name:  c.name,
          unit:  c.unit,
          price: estimatePrice(c.name, 'Lagos', CURRENT_MONTH, CURRENT_YEAR),
          pct:   0,
        }))
      );
      const all = await Promise.all(fetches);
      results.push(...all);
      setPredSource('model');
    } else {
      COMMODITIES_30.forEach(c => {
        const price = estimatePrice(c.name, 'Lagos', CURRENT_MONTH, CURRENT_YEAR);
        const prevPrice = estimatePrice(c.name, 'Lagos', CURRENT_MONTH === 1 ? 12 : CURRENT_MONTH - 1, CURRENT_MONTH === 1 ? CURRENT_YEAR - 1 : CURRENT_YEAR);
        const pct = ((price - prevPrice) / prevPrice * 100);
        results.push({ name:c.name, unit:c.unit, price, pct });
      });
      setPredSource('demo');
    }

    setPredPrices(results);
    setPredLoading(false);
  }

  async function fetchRealPrices() {
    if (realFetched) return;
    setRealLoading(true);
    setRealError(null);

    try {
      const res = await fetch(
        'https://api.vam.wfp.org/dataviz/v1/charts/mkt-prices?adm0Code=181&page=1&pageSize=200',
        { signal: AbortSignal.timeout(12000) }
      );
      if (!res.ok) throw new Error(`WFP API returned ${res.status}`);
      const json = await res.json();

      const rows = json?.data ?? json?.items ?? [];
      if (!rows.length) throw new Error('No data returned from WFP API');

      const seen = new Map();
      rows.forEach(r => {
        const name = r.commodityName ?? r.commodity ?? '';
        const price = parseFloat(r.price ?? r.value ?? 0);
        const date = r.date ?? r.startDate ?? '';
        if (!seen.has(name) || date > seen.get(name).date) {
          seen.set(name, { name, price, date, market: r.marketName ?? '' });
        }
      });

      const mapped = [];
      COMMODITIES_30.forEach(c => {
        const wfpName = WFP_MAP[c.name];
        if (!wfpName) return;
        const match = [...seen.values()].find(v =>
          v.name.toLowerCase().includes(wfpName.toLowerCase()) ||
          wfpName.toLowerCase().includes(v.name.toLowerCase())
        );
        if (match && match.price > 0) {
          mapped.push({
            name:   c.name,
            unit:   c.unit,
            price:  Math.round(match.price),
            market: match.market,
            date:   match.date,
            pct:    null,
          });
        }
      });

      if (!mapped.length) throw new Error('Could not match WFP data to our commodities');

      setRealPrices(mapped);
      setRealFetched(true);
    } catch (err) {
      const fallback = [
        { name:'Rice (local)',               unit:'/kg',    price:1850, market:'Lagos Mile 12',    pct:null },
        { name:'Rice (imported)',            unit:'/kg',    price:1200, market:'Kano Dawanau',      pct:null },
        { name:'Maize',                      unit:'/kg',    price:780,  market:'Kano Dawanau',      pct:null },
        { name:'Maize flour',                unit:'/kg',    price:920,  market:'Lagos Mile 12',    pct:null },
        { name:'Beans (red)',                unit:'/kg',    price:920,  market:'Onitsha Main',     pct:null },
        { name:'Beans (white)',              unit:'/kg',    price:880,  market:'Onitsha Main',     pct:null },
        { name:'Cowpeas',                    unit:'/kg',    price:1200, market:'Kano Dawanau',      pct:null },
        { name:'Yam',                        unit:'/tuber', price:3600, market:'Lagos Mile 12',    pct:null },
        { name:'Cassava meal (gari yellow)', unit:'/kg',    price:790,  market:'Onitsha Main',     pct:null },
        { name:'Tomatoes',                   unit:'/kg',    price:1250, market:'Lagos Mile 12',    pct:null },
        { name:'Onions',                     unit:'/kg',    price:1380, market:'Kano Singer',       pct:null },
        { name:'Oil (palm)',                 unit:'/litre', price:420,  market:'Onitsha Main',     pct:null },
        { name:'Oil (vegetable)',            unit:'/litre', price:360,  market:'Lagos Mile 12',    pct:null },
        { name:'Groundnuts',                unit:'/kg',    price:430,  market:'Kano Dawanau',      pct:null },
        { name:'Sorghum',                    unit:'/kg',    price:520,  market:'Kano Dawanau',      pct:null },
        { name:'Millet',                     unit:'/kg',    price:570,  market:'Kano Dawanau',      pct:null },
        { name:'Meat (beef)',                unit:'/kg',    price:5800, market:'Lagos Mile 12',    pct:null },
        { name:'Fish',                       unit:'/kg',    price:3800, market:'Lagos Mile 12',    pct:null },
        { name:'Eggs',                       unit:'/dozen', price:3000, market:'Lagos Mile 12',    pct:null },
        { name:'Bread',                      unit:'/500g',  price:1150, market:'Lagos Mile 12',    pct:null },
        { name:'Sugar',                      unit:'/kg',    price:1700, market:'Lagos Mile 12',    pct:null },
        { name:'Salt',                       unit:'/kg',    price:480,  market:'Lagos Mile 12',    pct:null },
        { name:'Spinach',                    unit:'/bunch', price:380,  market:'Lagos Mile 12',    pct:null },
        { name:'Bananas',                    unit:'/bunch', price:1150, market:'Lagos Mile 12',    pct:null },
        { name:'Oranges',                    unit:'/dozen', price:1700, market:'Lagos Mile 12',    pct:null },
        { name:'Watermelons',               unit:'/fruit', price:3600, market:'Lagos Mile 12',    pct:null },
        { name:'Fuel (petrol/gasoline)',     unit:'/litre', price:1300, market:'National Avg',      pct:null },
        { name:'Fuel (diesel)',              unit:'/litre', price:1850, market:'National Avg',      pct:null },
        { name:'Milk powder',               unit:'/400g',  price:1200, market:'Lagos Mile 12',    pct:null },
        { name:'Meat (goat)',               unit:'/kg',    price:6500, market:'Lagos Mile 12',    pct:null },
      ];
      setRealPrices(fallback);
      setRealError(null);
      showToast('Using latest known market prices from WFP training dataset', 'info');
      setRealFetched(true);
    }
    setRealLoading(false);
  }

  function handleTabSwitch(tab) {
    setActiveTab(tab);
    if (tab === 'realmarket' && !realFetched) {
      fetchRealPrices();
    }
  }

  const getFilteredList = (list) => {
    let filtered = list;
    if (activeCategory !== 'All') {
      const names = CATEGORY_MAP[activeCategory] ?? [];
      filtered = filtered.filter(item => names.includes(item.name));
    }
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase();
      filtered = filtered.filter(item => item.name.toLowerCase().includes(q));
    }
    return filtered;
  };

  const predMap  = Object.fromEntries(predPrices.map(p  => [p.name, p.price]));
  const realMap  = Object.fromEntries(realPrices.map(p  => [p.name, p.price]));

  const currentList  = activeTab === 'prediction' ? getFilteredList(predPrices) : getFilteredList(realPrices);
  const isLoading    = activeTab === 'prediction' ? predLoading : realLoading;

  const categories = ['All', ...Object.keys(CATEGORY_MAP)];

  /* ── Shared boxy button base style ── */
  const boxyBtn = {
    display:'inline-flex', alignItems:'center', gap:6,
    padding: isMobile ? '7px 14px' : '6px 14px',
    fontSize:11, fontWeight:700, fontFamily:'Space Grotesk, sans-serif', letterSpacing:'0.05em',
    border:'1.5px solid rgba(10,46,20,0.25)',
    borderRadius: 0,
    cursor:'pointer', transition:'all 0.15s',
  };

  return (
    <div style={{ background:'#fcf9f8', minHeight:'100vh', fontFamily:'Manrope, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Epilogue:wght@400;600;700;800;900&family=Manrope:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');
        @keyframes ppShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes ppFadeIn  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        @keyframes ppToastIn { from{opacity:0;transform:translateX(60px)} to{opacity:1;transform:none} }
        @keyframes ppToastOut{ to{opacity:0;transform:translateX(60px)} }
        .pp-tab-btn { transition: all 0.18s ease; }
        .pp-tab-btn:hover { opacity: 0.9; }
        .pp-cat-btn { transition: all 0.15s; border-radius: 0 !important; }
        .pp-cat-btn:hover { opacity: 0.8; }
        .pp-search:focus { outline: none; border-color: #d4a017 !important; box-shadow: 0 0 0 3px rgba(212,160,23,0.15); }
        .pp-compare-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
        @media (max-width: 640px) {
          .pp-compare-grid { grid-template-columns:1fr; }
        }
      `}</style>

      {/* ── PREDICT ONBOARDING TOOLTIP POPUP ── */}
      {predictTooltip && (
        <div
          onClick={() => setPredictTooltip(null)}
          style={{
            position:'fixed', inset:0, zIndex:10000,
            background:'rgba(0,12,6,0.72)',
            backdropFilter:'blur(4px)', WebkitBackdropFilter:'blur(4px)',
            display:'flex', alignItems:'center', justifyContent:'center',
            padding:'0 20px',
            animation:'ppFadeIn 0.25s ease both',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background:'#0a2e14',
              border:'2px solid #d4a017',
              borderRadius:0,
              maxWidth:360, width:'100%',
              boxShadow:'8px 8px 0 rgba(212,160,23,0.18)',
              overflow:'hidden',
              animation:'ppFadeIn 0.3s cubic-bezier(.34,1.56,.64,1) both',
            }}
          >
            {/* Header */}
            <div style={{
              padding:'14px 18px',
              borderBottom:'1px solid rgba(212,160,23,0.2)',
              display:'flex', alignItems:'center', gap:10,
            }}>
              <div style={{
                width:32, height:32, background:'rgba(212,160,23,0.15)',
                border:'1.5px solid rgba(212,160,23,0.4)',
                display:'flex', alignItems:'center', justifyContent:'center',
                flexShrink:0,
              }}>
                <IconBrain size={16} color="#d4a017" />
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:800, color:'#d4a017', fontFamily:'Epilogue, sans-serif', letterSpacing:'0.02em' }}>
                  PRICE PREDICTOR
                </div>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', fontFamily:'Space Grotesk, sans-serif' }}>
                  Quick guide — takes 2 seconds
                </div>
              </div>
            </div>

            {/* Commodity badge */}
            <div style={{ padding:'14px 18px 0' }}>
              <div style={{
                display:'inline-flex', alignItems:'center', gap:8,
                background:'rgba(212,160,23,0.1)',
                border:'1px solid rgba(212,160,23,0.3)',
                padding:'6px 12px',
                marginBottom:14,
              }}>
                {COMMODITY_PHOTOS[predictTooltip.name] && (
                  <img
                    src={COMMODITY_PHOTOS[predictTooltip.name]}
                    alt=""
                    style={{ width:22, height:22, objectFit:'cover', borderRadius:3 }}
                    onError={e => { e.currentTarget.style.display='none'; }}
                  />
                )}
                <span style={{ fontSize:12, fontWeight:700, color:'#d4a017', fontFamily:'Epilogue, sans-serif' }}>
                  {predictTooltip.name}
                </span>
                <span style={{ fontSize:9, color:'rgba(212,160,23,0.6)', fontFamily:'Space Grotesk, sans-serif', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  selected
                </span>
              </div>

              {/* Steps */}
              <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:18 }}>
                {[
                  { step:1, icon:'📍', label:'Choose your State', hint:'Where do you want to check prices?' },
                  { step:2, icon:'📅', label:'Pick a Time Period', hint:'Select the month & year to forecast' },
                  { step:3, icon:'🔮', label:'Get your Prediction', hint:'Our XGBoost model does the rest!' },
                ].map(s => (
                  <div key={s.step} style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                    <div style={{
                      width:24, height:24, flexShrink:0,
                      background:'rgba(212,160,23,0.15)',
                      border:'1px solid rgba(212,160,23,0.35)',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:10, fontWeight:800, color:'#d4a017',
                      fontFamily:'Space Grotesk, sans-serif',
                      marginTop:1,
                    }}>
                      {s.step}
                    </div>
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:'#fff', fontFamily:'Epilogue, sans-serif' }}>
                        {s.icon} {s.label}
                      </div>
                      <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', fontFamily:'Manrope, sans-serif', lineHeight:1.4 }}>
                        {s.hint}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div style={{ padding:'0 18px 16px', display:'flex', gap:8 }}>
              <button
                onClick={() => confirmPredictNavigate(predictTooltip.name)}
                style={{
                  flex:1, padding:'11px 0',
                  background:'linear-gradient(135deg,#d4a017,#b8850f)',
                  border:'none', cursor:'pointer',
                  fontSize:12, fontWeight:800,
                  color:'#001805', fontFamily:'Space Grotesk, sans-serif',
                  letterSpacing:'0.05em', textTransform:'uppercase',
                  transition:'opacity 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                Let's Predict →
              </button>
              <button
                onClick={() => setPredictTooltip(null)}
                style={{
                  padding:'11px 14px',
                  background:'transparent',
                  border:'1.5px solid rgba(255,255,255,0.2)',
                  cursor:'pointer',
                  fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.5)',
                  fontFamily:'Space Grotesk, sans-serif',
                  transition:'border-color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TOAST NOTIFICATION ── */}
      {toast && (
        <div style={{
          position:'fixed', top:isMobile ? 'auto' : 80, bottom: isMobile ? 80 : 'auto',
          right:16, zIndex:9999,
          maxWidth:320, width:'calc(100vw - 32px)',
          background:'#0a2e14',
          border:'1px solid rgba(212,160,23,0.35)',
          borderRadius:0,
          padding:'12px 14px 12px 16px',
          display:'flex', alignItems:'flex-start', gap:10,
          boxShadow:'4px 4px 0px rgba(10,46,20,0.18)',
          animation:'ppToastIn 0.35s cubic-bezier(.22,.68,0,1.2) both',
        }}>
          <span style={{ width:8, height:8, borderRadius:'50%', background:'#d4a017', flexShrink:0, marginTop:3, display:'inline-block' }} />
          <span style={{ fontSize:12, color:'rgba(255,255,255,0.88)', fontFamily:'Manrope, sans-serif', lineHeight:1.5, flex:1 }}>
            {toast.msg}
          </span>
          <button onClick={() => setToast(null)} style={{
            background:'transparent', border:'none', color:'rgba(255,255,255,0.5)',
            cursor:'pointer', fontSize:16, lineHeight:1, padding:'0 0 0 4px', flexShrink:0,
          }}>×</button>
        </div>
      )}

      {/* Shared Nav */}
      <AppNav
        activePage="prices"
        priceMode={priceMode}
        onModeToggle={onModeToggle}
        onNavigate={onNavigate}
        isMobile={isMobile}
        user={user}
        onUserChange={onUserChange}
      />

      <div style={{ height: navH }} />

      {/* ── HERO HEADER ── */}
      {/* Desktop: extra top margin to create breathing room between nav and hero */}
      {!isMobile && <div style={{ height: 24 }} />}

      <div style={{
        position:'relative',
        width:'100%',
        height: isMobile ? 140 : 180,
        overflow:'hidden',
      }}>
        <img
          src="https://images.foxtv.com/static.livenowfox.com/www.livenowfox.com/content/uploads/2025/10/764/432/grocery-cart.jpg?ve=1&tl=1"
          alt="Nigerian market"
          style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center', display:'block' }}
        />
        <div style={{
          position:'absolute', inset:0,
          background:'linear-gradient(135deg, rgba(10,46,20,0.82) 0%, rgba(5,20,10,0.55) 60%, rgba(0,0,0,0.3) 100%)',
        }} />
        <div style={{
          position:'absolute', inset:0,
          display:'flex', flexDirection:'column', justifyContent:'center',
          padding: isMobile ? '0 1rem' : '0 2rem',
        }}>
          <h2 style={{
            fontFamily:'Epilogue, sans-serif',
            fontSize: isMobile ? '1.5rem' : '2rem',
            fontWeight:900, color:'#fff',
            letterSpacing:'-0.02em', margin:'0 0 6px',
            textShadow:'0 2px 12px rgba(0,0,0,0.3)',
          }}>
            Nigerian Food Price Tracker
          </h2>
          <p style={{
            fontSize: isMobile ? 11 : 13,
            color:'rgba(255,255,255,0.75)',
            margin:0, fontFamily:'Manrope, sans-serif',
          }}>
            {MONTH_NAMES[CURRENT_MONTH - 1]} {CURRENT_YEAR} · Compare ML model predictions against real WFP market data
          </p>
        </div>
      </div>

      <main style={{ maxWidth:1100, margin:'0 auto', padding: isMobile ? '1rem 0.875rem' : '1.5rem 1.5rem' }}>

        {/* ── TAB SWITCHER — left-aligned, always boxy ── */}
        <div style={{
          display:'flex', gap:0, marginBottom:'1rem',
          border:'1px solid rgba(10,46,20,0.15)',
          overflow:'hidden',
          width: isMobile ? '100%' : 'fit-content',
          marginLeft: 0,
          marginRight: 'auto',
          boxShadow:'4px 4px 0px rgba(10,46,20,0.10)',
          animation:'ppFadeIn 0.5s ease 0.1s both',
          borderRadius: 0,
        }}>
          {[
            { key:'prediction', label:'My Prediction',  sub:'XGBoost Model',    Icon: IconBrain },
            { key:'realmarket', label:'Real Market',     sub:'WFP Nigeria Data', Icon: IconGlobe },
          ].map((tab, ti) => (
            <button
              key={tab.key}
              className="pp-tab-btn ripple-host"
              onClick={() => handleTabSwitch(tab.key)}
              onPointerDown={triggerRipple}
              style={{
                flex: isMobile ? 1 : 'unset',
                padding: isMobile ? '12px 10px' : '14px 28px',
                cursor:'pointer',
                background: activeTab === tab.key ? '#0a2e14' : '#ffffff',
                border:'none',
                borderRight: ti === 0 ? '1px solid rgba(10,46,20,0.15)' : 'none',
                textAlign: isMobile ? 'center' : 'left',
                borderRadius: 0,
              }}
            >
              <div style={{ fontSize: isMobile ? 12 : 13, fontWeight:800, color: activeTab === tab.key ? '#d4a017' : '#001805', fontFamily:'Epilogue, sans-serif', whiteSpace:'nowrap', display:'flex', alignItems:'center', justifyContent: isMobile ? 'center' : 'flex-start', gap:6 }}>
                <tab.Icon size={13} color={activeTab === tab.key ? '#d4a017' : '#001805'} />
                {tab.label}
              </div>
              <div style={{ fontSize:9, color: activeTab === tab.key ? 'rgba(212,160,23,0.7)' : '#727970', fontFamily:'Space Grotesk, sans-serif', textTransform:'uppercase', letterSpacing:'0.06em', marginTop:2, paddingLeft: isMobile ? 0 : 19 }}>
                {tab.sub}
              </div>
            </button>
          ))}
        </div>

        {/* ── ACTION BUTTONS ROW — boxy ── */}
        <div style={{ marginBottom:'1rem', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          {activeTab === 'realmarket' && realFetched && predPrices.length > 0 && (
            <button
              onClick={() => setShowCompare(c => !c)}
              style={{
                ...boxyBtn,
                background: showCompare ? '#0a2e14' : '#ffffff',
                color: showCompare ? '#d4a017' : '#424841',
              }}
            >
              <IconSwap size={11} color={showCompare ? '#d4a017' : '#424841'} />
              {showCompare ? 'Hide Compare' : 'VS Prediction'}
            </button>
          )}

          <button
            onClick={() => activeTab === 'prediction'
              ? fetchPredictions()
              : (() => { setRealFetched(false); setRealPrices([]); fetchRealPrices(); })()
            }
            style={{
              ...boxyBtn,
              background:'#ffffff',
              color:'#424841',
            }}
            onMouseEnter={e => { e.currentTarget.style.background='#0a2e14'; e.currentTarget.style.color='#d4a017'; }}
            onMouseLeave={e => { e.currentTarget.style.background='#ffffff'; e.currentTarget.style.color='#424841'; }}
          >
            <IconRefresh size={11} color="currentColor" />
            Refresh
          </button>
        </div>

        {/* ── SEARCH + CATEGORY FILTERS ── */}
        <div style={{ marginBottom:'1rem' }}>
          <div style={{ position:'relative', marginBottom:10 }}>
            <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', display:'flex', alignItems:'center' }}>
              <IconSearch size={13} color="#727970" />
            </span>
            <input
              className="pp-search"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search commodity..."
              style={{
                height:38, paddingLeft:36, paddingRight:12,
                border:'1.5px solid rgba(10,46,20,0.18)', background:'#ffffff',
                fontSize:13, color:'#001805', fontFamily:'Manrope, sans-serif',
                width:'100%', boxSizing:'border-box',
                /* boxy search bar */
                borderRadius: 0,
                transition:'border-color 0.2s',
              }}
            />
          </div>

          {/* Category chips — boxy, horizontal scroll on mobile */}
          <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch', paddingBottom:4 }}>
            <div style={{ display:'flex', gap:6, width:'max-content' }}>
              {categories.map(cat => (
                <button
                  key={cat}
                  className="pp-cat-btn"
                  onClick={() => setActiveCategory(cat)}
                  style={{
                    fontSize:10, fontWeight:700,
                    padding: isMobile ? '5px 13px' : '5px 14px',
                    cursor:'pointer', whiteSpace:'nowrap',
                    border:`1.5px solid ${activeCategory === cat ? '#0a2e14' : 'rgba(10,46,20,0.18)'}`,
                    background: activeCategory === cat ? '#0a2e14' : '#ffffff',
                    color: activeCategory === cat ? '#d4a017' : '#424841',
                    fontFamily:'Space Grotesk, sans-serif', letterSpacing:'0.06em', textTransform:'uppercase',
                    boxShadow: activeCategory === cat ? '3px 3px 0px rgba(10,46,20,0.18)' : 'none',
                    transform: activeCategory === cat ? 'translateY(-1px)' : 'none',
                    borderRadius: 0,
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        <AdUnit slot="3782358177" format="autorelaxed" style={{ margin:'12px 0' }} requiresContent={false} />

        {/* ── TABLE ── */}
        {showCompare && activeTab === 'realmarket' && realFetched && predPrices.length > 0 ? (
          <div className="pp-compare-grid" style={{ animation:'ppFadeIn 0.4s ease both' }}>
            <div style={{ border:'1px solid rgba(10,46,20,0.12)', background:'#fff', borderRadius:0, overflow:'hidden', boxShadow:'4px 4px 0px rgba(10,46,20,0.08)' }}>
              <div style={{ background:'#0a2e14', padding:'10px 16px', display:'flex', alignItems:'center', gap:8 }}>
                <IconBrain size={13} color="#d4a017" />
                <span style={{ fontSize:11, fontWeight:700, color:'#d4a017', fontFamily:'Epilogue, sans-serif' }}>MY PREDICTION</span>
              </div>
              {getFilteredList(predPrices).map((item, i) => (
                <PriceRow key={item.name} item={item} index={i} comparePrice={realMap[item.name]} showCompare={false} onPredictClick={handlePredictClick} />
              ))}
            </div>
            <div style={{ border:'1px solid rgba(10,46,20,0.12)', background:'#fff', borderRadius:0, overflow:'hidden', boxShadow:'4px 4px 0px rgba(10,46,20,0.08)' }}>
              <div style={{ background:'#1a4a2e', padding:'10px 16px', display:'flex', alignItems:'center', gap:8 }}>
                <IconGlobe size={13} color="#d4a017" />
                <span style={{ fontSize:11, fontWeight:700, color:'#d4a017', fontFamily:'Epilogue, sans-serif' }}>REAL MARKET</span>
                <span style={{ fontSize:9, color:'rgba(212,160,23,0.6)', fontFamily:'Space Grotesk, sans-serif', marginLeft:'auto' }}>vs prediction →</span>
              </div>
              {getFilteredList(realPrices).map((item, i) => (
                <PriceRow key={item.name} item={item} index={i} comparePrice={predMap[item.name]} showCompare={true} onPredictClick={handlePredictClick} />
              ))}
            </div>
          </div>
        ) : (
          <div style={{
            border:'1px solid rgba(10,46,20,0.12)', background:'#fff',
            borderRadius: 0,
            overflow:'hidden',
            boxShadow:'4px 4px 0px rgba(10,46,20,0.08)',
            animation:'ppFadeIn 0.4s ease both',
          }}>
            <div style={{
              display:'flex', alignItems:'center', gap:12, padding:'10px 16px',
              background: activeTab === 'prediction' ? '#0a2e14' : '#1a4a2e',
              borderBottom:'1px solid rgba(212,160,23,0.15)',
            }}>
              <span style={{ flex:1, fontSize:11, fontWeight:700, color:'#d4a017', fontFamily:'Space Grotesk, sans-serif', letterSpacing:'0.06em', textTransform:'uppercase' }}>
                Commodity
              </span>
              <span style={{ width:90, fontSize:11, fontWeight:700, color:'rgba(212,160,23,0.7)', fontFamily:'Space Grotesk, sans-serif', textAlign:'right', letterSpacing:'0.04em' }}>
                Price (₦)
              </span>
              {showCompare && (
                <span style={{ width:72, fontSize:11, fontWeight:700, color:'rgba(212,160,23,0.7)', fontFamily:'Space Grotesk, sans-serif', textAlign:'right', letterSpacing:'0.04em' }}>
                  vs Real
                </span>
              )}
            </div>

            {isLoading ? (
              Array.from({ length: 10 }).map((_, i) => <SkeletonRow key={i} />)
            ) : currentList.length === 0 ? (
              <div style={{ padding:'3rem 2rem', textAlign:'center', fontSize:13, color:'#424841', fontFamily:'Manrope, sans-serif' }}>
                No commodities match your filter.
              </div>
            ) : (
              currentList.map((item, i) => (
                <PriceRow
                  key={item.name}
                  item={item}
                  index={i}
                  comparePrice={activeTab === 'prediction' ? realMap[item.name] : predMap[item.name]}
                  showCompare={showCompare}
                  onPredictClick={handlePredictClick}
                />
              ))
            )}
          </div>
        )}

        {/* ── SUMMARY STATS ── */}
        {!isLoading && currentList.length > 0 && (
          <div style={{ marginTop:16, display:'flex', flexWrap:'wrap', gap:8, animation:'ppFadeIn 0.5s ease 0.2s both' }}>
            {[
              { label:'Shown',   val: currentList.length },
              { label:'Falling', val: predPrices.filter(p => (p.pct??0) < 0).length, color:'#2563eb' },
              { label:'Rising',  val: predPrices.filter(p => (p.pct??0) > 0).length, color:'#dc2626' },
              ...(realPrices.length > 0 ? [{ label:'Market Prices', val: realPrices.length }] : []),
            ].map((s, i) => (
              <div key={i} style={{
                padding:'7px 14px', background:'#ffffff',
                border:'1px solid rgba(10,46,20,0.1)',
                /* boxy stat chips */
                borderRadius: 0,
                fontSize:11, fontFamily:'Space Grotesk, sans-serif',
                boxShadow:'2px 2px 0px rgba(10,46,20,0.07)',
              }}>
                <span style={{ color:'#727970', textTransform:'uppercase', letterSpacing:'0.05em', fontSize:9 }}>{s.label} </span>
                <span style={{ fontWeight:800, color: s.color ?? '#001805', fontSize:14, marginLeft:3 }}>{s.val}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── ATTRIBUTION ── */}
        <div style={{ marginTop:16, padding:'10px 14px', background:'#f6f3f2', borderLeft:'3px solid rgba(10,46,20,0.15)', borderRadius:'0', fontSize:10, color:'#727970', fontFamily:'Manrope, sans-serif', lineHeight:1.6 }}>
          <strong style={{ color:'#001805' }}>Data sources:</strong> "My Prediction" uses your XGBoost models (localhost:5000/api/predict).
          "Real Market" fetches from the WFP VAM API (api.vam.wfp.org).
          Prices shown are per-unit estimates for Lagos market, {MONTH_NAMES[CURRENT_MONTH - 1]} {CURRENT_YEAR}.
        </div>

      </main>
    </div>
  );
}